import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Descriptions, Drawer, Empty, Form, Grid, Modal, Space, Spin, Table, Tabs, Tag, Timeline, Upload, message } from "antd";
import { DownloadOutlined, EyeOutlined, InboxOutlined, ReloadOutlined, UndoOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DatosActaFields, { aDatosActa } from "./DatosActaFields";
import EstadoManifestacion from "./EstadoManifestacion";
import { descargarDocumento } from "./VisorPdfModal";
import {
  cargarActaFirmada,
  editarManifestacion,
  generarNuevaVersion,
  getDetalle,
  getHistorial,
  mensajeDeError,
  restaurarManifestacion,
} from "../../../services/manifestacionesInteres/manifestacionesInteresService";

const TAMANO_MAXIMO_MB = 20;
const ANCHO_DRAWER = 860;

const ACCIONES = {
  CREACION: { texto: "Creación", color: "blue" },
  EDICION: { texto: "Edición", color: "orange" },
  GENERACION: { texto: "Generación del PDF", color: "blue" },
  REGENERACION: { texto: "Nueva versión del PDF", color: "blue" },
  CARGA_FIRMADA: { texto: "Carga del acta firmada", color: "green" },
  REEMPLAZO_FIRMADA: { texto: "Reemplazo del acta firmada", color: "green" },
  CAMBIO_ESTADO: { texto: "Cambio de estado", color: "green" },
  ELIMINACION: { texto: "Eliminación", color: "red" },
  RESTAURACION: { texto: "Restauración", color: "blue" },
};

const ETIQUETAS_CAMPO = {
  fecha_manifestacion: "Fecha de manifestación",
  ciudad_expedicion: "Ciudad de expedición",
  telefono_acta: "Teléfono",
  nombre_profesional: "Profesional",
  oid_proyecto: "Proyecto (id)",
  oid_participante: "Participante (id)",
  version: "Versión",
  archivo: "Archivo",
  nombre_original: "Nombre original",
  estado: "Estado",
  motivo: "Motivo",
};

const ETIQUETAS_ESTADO = { PENDIENTE_CARGA: "Pendiente de carga", ACTA_CARGADA: "Acta cargada" };

const valorHistorial = (campo, valor) => {
  if (valor === null || valor === undefined) return "vacío";
  return campo === "estado" ? ETIQUETAS_ESTADO[valor] || valor : String(valor);
};

const formatoFecha = (valor) => (valor ? dayjs(valor).format("DD/MM/YYYY HH:mm") : "—");
const formatoTamano = (bytes) => `${(Number(bytes) / (1024 * 1024)).toFixed(2)} MB`;

const CambiosHistorial = ({ anteriores, nuevos }) => {
  const campos = [...new Set([...Object.keys(anteriores || {}), ...Object.keys(nuevos || {})])];
  if (campos.length === 0) return null;
  return (
    <ul className="mb-0 ps-3" style={{ fontSize: 13 }}>
      {campos.map((campo) => (
        <li key={campo}>
          <b>{ETIQUETAS_CAMPO[campo] || campo}:</b>{" "}
          {anteriores && campo in anteriores && <><s>{valorHistorial(campo, anteriores[campo])}</s> → </>}
          {valorHistorial(campo, nuevos?.[campo])}
        </li>
      ))}
    </ul>
  );
};

export default function DetalleManifestacionDrawer({ oid, permisos, pestana, onClose, onCambio, onVerDocumento }) {
  const pantallas = Grid.useBreakpoint();
  const [formEdicion] = Form.useForm();
  const [detalle, setDetalle] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [pestanaActiva, setPestanaActiva] = useState("datos");
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [datos, eventos] = await Promise.all([
        getDetalle(oid),
        permisos.historial ? getHistorial(oid) : Promise.resolve([]),
      ]);
      setDetalle(datos);
      setHistorial(eventos);
    } catch (e) {
      message.error(await mensajeDeError(e, "No se pudo cargar la manifestación."));
    } finally {
      setCargando(false);
    }
  }, [oid, permisos.historial]);

  useEffect(() => {
    if (!oid) {
      setDetalle(null);
      setHistorial([]);
      return;
    }
    setPestanaActiva(pestana || "datos");
    cargar();
  }, [oid, pestana, cargar]);

  const trasCambio = async () => {
    await cargar();
    onCambio();
  };

  const valoresEdicion = detalle && {
    ciudad_expedicion: detalle.ciudad_expedicion,
    telefono_acta: detalle.telefono_acta || "",
    nombre_profesional: detalle.nombre_profesional,
    fecha_manifestacion: dayjs(detalle.fecha_manifestacion),
  };

  const guardarEdicion = async () => {
    const valores = await formEdicion.validateFields();
    setGuardando(true);
    try {
      await editarManifestacion(oid, aDatosActa(valores));
      setEditando(false);
      message.success("Cambios guardados. El PDF no cambia hasta generar una nueva versión.");
      await trasCambio();
    } catch (e) {
      message.error(await mensajeDeError(e, "No se pudieron guardar los cambios."));
    } finally {
      setGuardando(false);
    }
  };

  const generar = () => {
    Modal.confirm({
      title: "¿Generar una nueva versión del PDF?",
      content: detalle.estado === "ACTA_CARGADA"
        ? "Las versiones anteriores se conservan. El acta firmada no cambia: sigue correspondiendo a la versión que se imprimió."
        : "Las versiones anteriores se conservan.",
      okText: "Generar",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await generarNuevaVersion(oid);
          message.success("Nueva versión generada.");
          await trasCambio();
        } catch (e) {
          message.error(await mensajeDeError(e, "No se pudo generar la nueva versión."));
        }
      },
    });
  };

  const subirArchivo = async (archivo) => {
    setSubiendo(true);
    try {
      await cargarActaFirmada(oid, archivo);
      message.success("Acta firmada cargada.");
      await trasCambio();
    } catch (e) {
      message.error(await mensajeDeError(e, "No se pudo cargar el acta firmada."));
    } finally {
      setSubiendo(false);
    }
  };

  const validarYSubir = (archivo) => {
    const esPdf = archivo.type === "application/pdf" || archivo.name.toLowerCase().endsWith(".pdf");
    if (!esPdf) {
      message.error("Solo se permiten archivos PDF.");
      return Upload.LIST_IGNORE;
    }
    if (archivo.size > TAMANO_MAXIMO_MB * 1024 * 1024) {
      message.error(`El archivo supera el máximo de ${TAMANO_MAXIMO_MB} MB.`);
      return Upload.LIST_IGNORE;
    }
    if (detalle.estado === "ACTA_CARGADA") {
      Modal.confirm({
        title: "Ya hay un acta firmada cargada",
        content: "El archivo nuevo será la versión vigente. El anterior se conserva.",
        okText: "Cargar nueva versión",
        cancelText: "Cancelar",
        onOk: () => subirArchivo(archivo),
      });
    } else {
      subirArchivo(archivo);
    }
    return Upload.LIST_IGNORE;
  };

  const restaurar = async () => {
    try {
      await restaurarManifestacion(oid);
      message.success("Manifestación restaurada.");
      await trasCambio();
    } catch (e) {
      message.error(await mensajeDeError(e, "No se pudo restaurar la manifestación."));
    }
  };

  const descargar = async (doc) => {
    try {
      await descargarDocumento(oid, doc);
    } catch (e) {
      message.error(await mensajeDeError(e, "No se pudo descargar el documento."));
    }
  };

  const eliminada = !!detalle?.eliminado_en;

  const columnasDocumentos = [
    { title: "Versión", dataIndex: "version", width: 80, render: (v) => `v${v}` },
    { title: "Archivo", dataIndex: "nombre_fisico", ellipsis: true },
    { title: "Fecha", dataIndex: "cargado_en", width: 150, render: formatoFecha, responsive: ["md"] },
    { title: "Usuario", dataIndex: "cargado_por_login", width: 120, responsive: ["lg"] },
    { title: "Tamaño", dataIndex: "tamano_bytes", width: 90, render: formatoTamano, responsive: ["lg"] },
    {
      title: "Acciones",
      key: "acciones",
      width: 110,
      render: (_, doc) => (
        <Space>
          <Button size="small" aria-label={`Ver ${doc.nombre_fisico}`} icon={<EyeOutlined />} onClick={() => onVerDocumento(oid, doc)} />
          {permisos.descargar && (
            <Button size="small" aria-label={`Descargar ${doc.nombre_fisico}`} icon={<DownloadOutlined />} onClick={() => descargar(doc)} />
          )}
        </Space>
      ),
    },
  ];

  const tablaDocumentos = (tipo) => (
    <Table
      rowKey="oid"
      size="small"
      pagination={false}
      columns={columnasDocumentos}
      dataSource={detalle.documentos.filter((d) => d.tipo === tipo)}
      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin documentos" /> }}
      className="mb-4"
    />
  );

  const pestanas = detalle && [
    {
      key: "datos",
      label: "Datos",
      children: editando ? (
        <Form form={formEdicion} layout="vertical" initialValues={valoresEdicion}>
          <DatosActaFields />
          <Space>
            <Button type="primary" loading={guardando} onClick={guardarEdicion}>Guardar</Button>
            <Button disabled={guardando} onClick={() => setEditando(false)}>Cancelar</Button>
          </Space>
        </Form>
      ) : (
        <>
          <Descriptions bordered size="small" column={1} className="mb-3">
            <Descriptions.Item label="Proyecto">{detalle.proyecto_codigo} - {detalle.proyecto}</Descriptions.Item>
            <Descriptions.Item label="Participante">{detalle.nombre_participante}</Descriptions.Item>
            <Descriptions.Item label="Documento">{detalle.tipo_identificacion} {detalle.numero_identificacion}</Descriptions.Item>
            <Descriptions.Item label="Estado"><EstadoManifestacion estado={detalle.estado} eliminada={eliminada} /></Descriptions.Item>
            <Descriptions.Item label="Ciudad de expedición">{detalle.ciudad_expedicion}</Descriptions.Item>
            <Descriptions.Item label="Fecha de manifestación">{dayjs(detalle.fecha_manifestacion).format("DD/MM/YYYY")}</Descriptions.Item>
            <Descriptions.Item label="Teléfono en el acta">{detalle.telefono_acta || "—"}</Descriptions.Item>
            <Descriptions.Item label="Profesional">{detalle.nombre_profesional}</Descriptions.Item>
            <Descriptions.Item label="Última generación">{formatoFecha(detalle.fecha_generacion)}</Descriptions.Item>
            <Descriptions.Item label="Última carga">{formatoFecha(detalle.fecha_carga)}</Descriptions.Item>
            <Descriptions.Item label="Creado por">{detalle.creado_por_nombre} ({formatoFecha(detalle.creado_en)})</Descriptions.Item>
          </Descriptions>
          {!eliminada && (
            <Space wrap>
              {permisos.editar && <Button onClick={() => setEditando(true)}>Editar datos</Button>}
              {permisos.generar && <Button icon={<ReloadOutlined />} onClick={generar}>Generar nuevamente</Button>}
            </Space>
          )}
          {eliminada && permisos.eliminar && (
            <Button icon={<UndoOutlined />} onClick={restaurar}>Restaurar</Button>
          )}
        </>
      ),
    },
    {
      key: "documentos",
      label: "Documentos",
      children: (
        <>
          <h6>Actas generadas</h6>
          {tablaDocumentos("GENERADA")}
          <h6>Actas firmadas</h6>
          {tablaDocumentos("FIRMADA")}
          {permisos.cargar && !eliminada && (
            <Upload.Dragger accept=".pdf,application/pdf" multiple={false} showUploadList={false} disabled={subiendo} beforeUpload={validarYSubir}>
              <p className="ant-upload-drag-icon">{subiendo ? <Spin /> : <InboxOutlined />}</p>
              <p className="ant-upload-text">
                {detalle.estado === "ACTA_CARGADA" ? "Cargar una nueva versión del acta firmada" : "Cargar el acta firmada"}
              </p>
              <p className="ant-upload-hint">Solo PDF, máximo {TAMANO_MAXIMO_MB} MB.</p>
            </Upload.Dragger>
          )}
        </>
      ),
    },
    ...(permisos.historial
      ? [{
        key: "historial",
        label: "Historial",
        children: (
          <Timeline
            items={historial.map((h) => ({
              key: h.oid,
              color: ACCIONES[h.accion]?.color || "gray",
              children: (
                <>
                  <b>{ACCIONES[h.accion]?.texto || h.accion}</b>
                  <div style={{ fontSize: 13, color: "#666" }}>
                    {formatoFecha(h.fecha)} · {h.usuario_nombre} ({h.usuario_login})
                  </div>
                  <CambiosHistorial anteriores={h.datos_anteriores} nuevos={h.datos_nuevos} />
                </>
              ),
            }))}
          />
        ),
      }]
      : []),
  ];

  return (
    <Drawer
      title={detalle ? `Manifestación · ${detalle.nombre_participante}` : "Manifestación"}
      open={!!oid}
      onClose={() => {
        setEditando(false);
        onClose();
      }}
      width={pantallas.md ? ANCHO_DRAWER : "100%"}
      destroyOnHidden
    >
      {cargando && !detalle && <div className="text-center py-5"><Spin /></div>}
      {detalle && (
        <>
          {eliminada && <Alert type="error" showIcon className="mb-3" message="Esta manifestación está eliminada. Sus documentos se conservan." />}
          {!eliminada && detalle.datos_desactualizados && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message="Los datos cambiaron después del último PDF."
              description="Genere una nueva versión para que el acta los refleje."
            />
          )}
          {!eliminada && detalle.firmada_desactualizada && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message="El acta firmada corresponde a una versión anterior del PDF."
              description={<>Hay una versión generada más reciente que la firmada. <Tag color="orange">Revise si debe firmarse de nuevo</Tag></>}
            />
          )}
          <Tabs activeKey={pestanaActiva} onChange={setPestanaActiva} items={pestanas} />
        </>
      )}
    </Drawer>
  );
}
